import { type PropsWithChildren, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PORTAL_IDLE_TIMEOUT } from './constants';
import { useIdleTimeout } from './hooks/useIdleTimeout';
import { useLocalLogout, useLogout } from './hooks/useLogout';
import './PortalLayout.less';
import { useSession } from '../common/authorization/context/Session';
import PageFooter from '../components/layout/PageFooter';
import { PortalHeader, TenantType } from './PortalHeader';
import { PortalPath } from './PortalRoutes';

const PortalLayout = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tenantType } = useSession();
  const userLogout = useLogout();
  const localLogout = useLocalLogout();

  const handleSettings = () => {
    const params = new URLSearchParams(searchParams);
    navigate({ pathname: PortalPath.SETTINGS, search: `?${params.toString()}` });
  };

  const handleLogout = async () => {
    if (tenantType === TenantType.TEST) {
      await localLogout();
    } else {
      await userLogout();
    }
  };

  const { startIdleTimer, stopIdleTimer } = useIdleTimeout(PORTAL_IDLE_TIMEOUT, localLogout);

  useEffect(() => {
    startIdleTimer();
    return () => {
      stopIdleTimer();
    };
  }, [startIdleTimer, stopIdleTimer]);

  const scrollYOff = window.location.pathname.startsWith(PortalPath.SETTINGS) ? 'scroll-y-off' : '';

  return (
    <div className="portal-layout">
      {/* Header */}
      <PortalHeader userLogout={handleLogout} handleSettings={handleSettings} />
      {/* Content */}
      <div className={`content ${scrollYOff}`}>{children}</div>
      {/* Footer */}
      <PageFooter />
    </div>
  );
};

export default PortalLayout;
